import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from '../css/clinical.module.css';
import '../css/pirogov-dev.css';

// ── Разделённые модули ──
import { bodyOptions, modeOptions, instructions, MAX_POLL_COUNT } from './pirogov-dev/constants';
import { getCookie, setCookieValue, blobToBase64, buildConversationHistory, downloadFile } from './pirogov-dev/utils';
import useAudioRecorder from './pirogov-dev/useAudioRecorder';
import useDualCtrlNav from './pirogov-dev/useDualCtrlNav';
import {
  UserBubble, AiBubble, AiThought, DownloadBtn, WaveformPlayer,
  ChatInputForm, InstructionsModal, BodySwitchModal, WelcomeMessage,
} from './pirogov-dev/components';
import AiThoughtsPanel from './pirogov-dev/AiThoughtsPanel';
import PhaseIndicator from './pirogov-dev/PhaseIndicator';
import microphoneIcon from '../img/agent/microphone.svg';
import trashIcon from '../img/agent/trash.svg';
import historyIcon from '../img/agent/history.svg';
import plusIcon from '../img/agent/plus.svg';
import maleIcon from '../img/agent/male.svg';
import femaleIcon from '../img/agent/female.svg';
import fileIcon from '../img/agent/file.svg';
import chatIcon from '../img/agent/chat.svg';
import menuIcon from '../img/agent/menu.svg';

const AnatomyAssistantPirogovDev = () => {
  useDualCtrlNav();
  // Парсим GET-параметр body (man/woman), по умолчанию мужское тело
  const [query, setQuery] = useState(() => '');
  const [selectedBody, setSelectedBody] = useState('structures.json');
  const [selectedMode, setSelectedMode] = useState(modeOptions[0].value);
  const [showBodySwitchConfirm, setShowBodySwitchConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [scenePlanningInfo, setScenePlanningInfo] = useState(null);
  const [error, setError] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [disabledColors, setDisabledColors] = useState(false); // kept for API compat, always false
  const [apiToken, setApiToken] = useState(() => getCookie('api_token') || '');
  const [requestsRemaining, setRequestsRemaining] = useState(null);
  const [showTokenInput, setShowTokenInput] = useState(() => !getCookie('api_token'));
  const [currentPhase, setCurrentPhase] = useState(null);
  const [currentAgent, setCurrentAgent] = useState(null);
  const [agentThoughts, setAgentThoughts] = useState([]);
  const agentThoughtsRef = useRef([]);

  // Обновление сцены
  const [updateQuery, setUpdateQuery] = useState('');
  const [showPlanningDetails, setShowPlanningDetails] = useState(true);
  const [savedPlanningDetails, setSavedPlanningDetails] = useState([]);
  // M3: инкрементальный накопитель labeled-деталей — сервер отдаёт лишь
  // скользящее окно (cap 50), финальные «Детали планирования» строим из него
  const seenPlanningDetailsRef = useRef([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updatedDownloadUrl, setUpdatedDownloadUrl] = useState(null);
  const [updateHistory, setUpdateHistory] = useState([]);
  const pendingUpdateQueryRef = useRef('');
  const pendingUpdateAudioRef = useRef(null);
  const isSubmittingRef = useRef(false);
  const rawPresetRef = useRef(null);
  const presetPathRef = useRef(null);

  // Синхронизация ref для доступа к мыслям внутри closure поллинга
  useEffect(() => { agentThoughtsRef.current = agentThoughts; }, [agentThoughts]);

  // Авто-скролл (секундомер фазы и «Думает…» — в PhaseIndicator, M7)
  const messagesEndRef = useRef(null);
  const isProcessing = loading || isUpdating;

  // M3: при каждом poll добавляем новые labeled-мысли (ключ label+text)
  // в накопитель — окно cap-50 на сервере больше не съедает ранние записи
  useEffect(() => {
    const seen = seenPlanningDetailsRef.current;
    if (!agentThoughts.length) return;
    const keys = new Set(seen.map(t => `${t.label}|${t.text}`));
    let added = false;
    for (const t of agentThoughts) {
      if (!t.label) continue;
      const key = `${t.label}|${t.text}`;
      if (!keys.has(key)) {
        keys.add(key);
        seen.push({ label: t.label, text: t.text });
        added = true;
      }
    }
    if (added) setSavedPlanningDetails([...seen]);
  }, [agentThoughts]);

  // Голосовое сообщение при генерации + ожидающий пузырь при редактировании
  const [submittedAudioUrl, setSubmittedAudioUrl] = useState(null);
  const [submittedAudioTranscription, setSubmittedAudioTranscription] = useState(null);
  const [submittedAudioBase64, setSubmittedAudioBase64] = useState(null);
  const [pendingUpdateBubble, setPendingUpdateBubble] = useState(null);

  // Уточняющие вопросы
  const [clarificationQuestion, setClarificationQuestion] = useState(null);
  const [clarificationHistory, setClarificationHistory] = useState([]);
  const [clarificationAnswer, setClarificationAnswer] = useState('');
  const [isClarificationForUpdate, setIsClarificationForUpdate] = useState(false);
  const [isOutOfScope, setIsOutOfScope] = useState(false);
  const [chatLocked, setChatLocked] = useState(false);
  const offTopicCountRef = useRef(0);
  const clarificationOriginalQueryRef = useRef('');
  const clarificationOriginalPayloadRef = useRef(null);
  const clarificationHistoryRef = useRef([]);
  const [initialGenClarifications, setInitialGenClarifications] = useState([]);
  const sessionIdRef = useRef(null);
  // dialogue_id от бэкенда: идентификатор папки диалога, переиспользуется как chat_id
  const dialogueIdRef = useRef(null);

  // История чатов
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [chatList, setChatList] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const chatIdRef = useRef(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // ── Аудио-рекордеры (один хук на каждый контекст) ──
  const handleAudioError = useCallback((msg) => setError(msg), []);
  const initialRecorder = useAudioRecorder(handleAudioError);
  const updateRecorder  = useAudioRecorder(handleAudioError);
  const clarRecorder    = useAudioRecorder(handleAudioError);

  const currentInstruction = instructions[selectedMode];

  const isInChatMode = !!(downloadUrl || loading || clarificationQuestion
    || clarificationHistory.length > 0 || initialGenClarifications.length > 0 || chatLocked);

const handleTokenSave = (token) => {
  const tokenToSave = token.trim();
  if (!tokenToSave) {
    setError('Пожалуйста, введите токен');
    return;
  }
  setCookieValue('api_token', tokenToSave);
  setApiToken(tokenToSave);
  setShowTokenInput(false);
  setError(null);
  
  // Загружаем статистику токена сразу после сохранения
  const loadStats = async () => {
    try {
      const response = await fetch('/api/v1/token-stats/', {
        method: 'GET',
        headers: {
          'X-CSRFToken': getCookie('csrftoken'),
          'X-Auth-Token': tokenToSave,
        },
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.rate_limit && data.rate_limit.remaining !== undefined) {
          setRequestsRemaining(data.rate_limit.remaining);
          console.log('✓ Token stats loaded:', data);
        }
      } else if (response.status === 401) {
        setError('❌ Неверный токен. Проверьте и попробуйте снова.');
        setShowTokenInput(true);
        setApiToken('');
        setCookieValue('api_token', '', -1);
      } else if (response.status === 502) {
        const text = await response.text();
        console.error('❌ Server error (502):', text);
        setError('❌ Ошибка сервера (502). Проверьте логи сервера.');
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Failed to load token stats:', response.status, errorData);
        setError(`❌ Ошибка при загрузке статистики (${response.status}): ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('❌ Error loading token stats:', err);
      setError(`❌ Ошибка при загрузке статистики: ${err.message}`);
    }
  };
  
  loadStats();
};

const handleTokenChange = (newToken) => {
  setApiToken(newToken);
  setCookieValue('api_token', newToken);
};

const handleTokenReset = () => {
  setApiToken('');
  setCookieValue('api_token', '', -1);
  setShowTokenInput(true);
  setRequestsRemaining(null);
};

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [downloadUrl, updateHistory.length, loading, isUpdating, clarificationQuestion]);

  // Загружаем информацию о лимитах при загрузке страницы, если пользователь включил токен
  useEffect(() => {
    const loadTokenStats = async () => {
      if (apiToken && !requestsRemaining) {
        try {
          const response = await fetch('/api/v1/token-stats/', {
            method: 'GET',
            headers: {
              'X-CSRFToken': getCookie('csrftoken'),
              'X-Auth-Token': apiToken,
            },
            credentials: 'include',
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.rate_limit && data.rate_limit.remaining !== undefined) {
              setRequestsRemaining(data.rate_limit.remaining);
              console.log('✓ Token stats loaded on page load:', data);
            }
          } else if (response.status === 401) {
            console.warn('❌ Token validation failed - likely expired or invalid');
            setShowTokenInput(true);
          } else if (response.status === 502) {
            const text = await response.text();
            console.error('❌ Server error (502 Bad Gateway):', text);
            setError('❌ Ошибка сервера (502). Проверьте логи на сервере.');
          } else {
            const errorData = await response.json().catch(() => ({}));
            console.error('❌ Error loading token stats:', response.status, errorData);
            setError(`❌ Ошибка ${response.status}: ${errorData.error || 'Unknown error'}`);
          }
        } catch (err) {
          console.error('❌ Network error loading token stats:', err);
          setError(`❌ Ошибка сети: ${err.message}`);
        }
      }
    };
    
    loadTokenStats();
  }, [apiToken]);

  // ── Глобальный слушатель пробела — запись голоса даже без фокуса на textarea ──
  const activeContextRef = useRef('initial');
  useEffect(() => {
    if (clarificationQuestion && !loading && !isUpdating && !chatLocked) {
      activeContextRef.current = 'clarification';
    } else if ((downloadUrl || loading) && !clarificationQuestion && !chatLocked) {
      activeContextRef.current = 'update';
    } else {
      activeContextRef.current = 'initial';
    }
  }, [clarificationQuestion, loading, isUpdating, chatLocked, downloadUrl]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== ' ' || e.repeat) return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      e.preventDefault();
      const ctx = activeContextRef.current;
      if (ctx === 'clarification') clarRecorder.startRecording(e);
      else if (ctx === 'update' && !isUpdating && !loading) updateRecorder.startRecording(e);
      else if (ctx === 'initial') initialRecorder.startRecording(e);
    };
    const handleKeyUp = (e) => {
      if (e.key !== ' ') return;
      if (initialRecorder.recordingRef.current) initialRecorder.stopRecording(e);
      if (updateRecorder.recordingRef.current) updateRecorder.stopRecording(e);
      if (clarRecorder.recordingRef.current) clarRecorder.stopRecording(e);
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [initialRecorder, updateRecorder, clarRecorder, isUpdating, loading]);

  const handleDownload = useCallback(async (url) => {
    try {
      await downloadFile(url, apiToken);
    } catch (err) {
      console.error('Download error:', err);
      setError('Ошибка при скачивании файла.');
    }
  }, [apiToken]);

  const pollCountRef = useRef(0);

  // R8: единая точка захвата идентификаторов диалога/сессии из ответов
  // сервера. Poll-ответы (task-status) несут поля на верхнем уровне:
  // data.session_id / data.result.dialogue_id; update-результат — в
  // data.result.dialogue_id. Синхронных ответов с top-level dialogue_id
  // нет (всегда 202 + polling), см. Q6.
  const captureIds = (data) => {
    if (data.session_id) sessionIdRef.current = data.session_id;
    const did = data.result?.dialogue_id || data.dialogue_id;
    if (did) dialogueIdRef.current = did;
  };

  const pollTaskStatus = (taskId, isUpdate = false) => {
    return new Promise((resolve) => {
      const poll = async () => {
        pollCountRef.current += 1;
        if (pollCountRef.current > MAX_POLL_COUNT) {
          console.error('[pollTaskStatus] Max poll count reached, stopping.');
          setError('Превышено время ожидания ответа. Попробуйте ещё раз.');
          setLoading(false);
          setIsUpdating(false);
          setPendingUpdateBubble(null);
          setCurrentPhase(null);
          setCurrentAgent(null);
          resolve();
          return;
        }

        try {
          const response = await fetch(`/api/v1/task-status/${taskId}/`, {
            method: 'GET',
            headers: {
              'X-CSRFToken': getCookie('csrftoken'),
              'X-Auth-Token': apiToken,
            },
            credentials: 'include',
          });

          if (!response.ok) {
            throw new Error(`Ошибка сервера: ${response.status}`);
          }

          const data = await response.json();
          console.log(`[pollTaskStatus] #${pollCountRef.current} status=${data.status}`, data);

          if (data.status === 'SUCCESS') {
            // Успешное завершение — сбрасываем счётчик off-topic
            offTopicCountRef.current = 0;
            // Capture backend session_id / dialogue_id (R8)
            captureIds(data);
            const result = data.result;
            if (result.download_url) {
              // Обновляем расшифровку аудио в последнем элементе clarificationHistory
              if (result.user_query) {
                const hist = clarificationHistoryRef.current;
                const lastEntry = hist[hist.length - 1];
                if (lastEntry && lastEntry.audioUrl && !lastEntry.answer) {
                  const updated = [...hist];
                  updated[updated.length - 1] = { ...lastEntry, answer: result.user_query };
                  clarificationHistoryRef.current = updated;
                  setClarificationHistory(updated);
                }
              }

              if (isUpdate) {
                // Сохраняем уточнения данного обновления внутри элемента истории, затем очищаем
                const rounds = [...clarificationHistoryRef.current];
                setUpdatedDownloadUrl(result.download_url);
                // Обновляем серверный путь к пресету
                presetPathRef.current = result.preset_path || presetPathRef.current;

                // Аудио-данные из pendingUpdateBubble для сохранения в историю
                const pendingAudio = pendingUpdateAudioRef.current;
                const updateAudioFields = {};
                if (pendingAudio) {
                  updateAudioFields.audioUrl = pendingAudio.audioUrl;
                  if (pendingAudio.audio) updateAudioFields.audio = pendingAudio.audio;
                  updateAudioFields.transcription = result.user_query || '';
                }

                if (result.scene_generated) {
                  // Новая сцена сгенерирована из update pipeline
                  const newScenePlanningInfo = result.scene_planning_info
                    ? {
                        ...result.scene_planning_info,
                        rationale: result.ai_response?.rationale || result.scene_planning_info?.rationale,
                        applied_mode: result.ai_response?.applied_mode || result.scene_planning_info?.applied_mode,
                        special_rules: result.ai_response?.special_rules || result.scene_planning_info?.special_rules,
                      }
                    : null;
                  setUpdateHistory(prev => [...prev, {
                    query: pendingUpdateQueryRef.current,
                    url: result.download_url,
                    rationale: result.generation_status_message || 'Новая сцена сгенерирована',
                    completionReport: null,
                    clarificationRounds: rounds,
                    sceneGenerated: true,
                    scenePlanningInfo: newScenePlanningInfo,
                    rawPresetFilename: result.raw_preset_filename || null,
                    ...updateAudioFields,
                  }]);
                  // Обновляем scene planning info для новой сцены
                  if (result.scene_planning_info) {
                    setScenePlanningInfo(result.scene_planning_info);
                  }
                } else {
                  const accumulatedThoughts = agentThoughtsRef.current
                    .filter(t => t.label)
                    .map(t => ({ label: t.label, text: t.text }));
                  setUpdateHistory(prev => [...prev, {
                    query: pendingUpdateQueryRef.current,
                    url: result.download_url,
                    savedThoughts: accumulatedThoughts,
                    completionReport: result.completion_report || null,
                    clarificationRounds: rounds,
                    rawPresetFilename: result.raw_preset_filename || null,
                    ...updateAudioFields,
                  }]);
                }
                setAgentThoughts([]);
                setClarificationHistory([]);
                clarificationHistoryRef.current = [];
                setPendingUpdateBubble(null);
                pendingUpdateAudioRef.current = null;
              } else {
                // Сохраняем уточнения первоначальной генерации в отдельный стейт
                setInitialGenClarifications([...clarificationHistoryRef.current]);
                setClarificationHistory([]);
                clarificationHistoryRef.current = [];
                setDownloadUrl(result.download_url);
                // Сохраняем серверный путь к пресету для update-scene
                presetPathRef.current = result.preset_path || null;
                // Сохраняем расшифровку аудио для первоначального запроса
                if (result.user_query) setSubmittedAudioTranscription(result.user_query);
                // Объединяем информацию из ai_response и scene_planning_info
                const fullScenePlanningInfo = {
                  ...result.scene_planning_info,
                  // Добавляем поля из ai_response
                  rationale: result.ai_response?.rationale,
                  applied_mode: result.ai_response?.applied_mode,
                  special_rules: result.ai_response?.special_rules,
                };
                setScenePlanningInfo(fullScenePlanningInfo);
                setSavedPlanningDetails([...seenPlanningDetailsRef.current]);
                rawPresetRef.current = result.raw_preset_filename || null;
                setAgentThoughts([]);
              }
              
              // Обновляем оставшиеся запросы из rate_limit
              if (result.rate_limit && result.rate_limit.remaining !== undefined) {
                setRequestsRemaining(result.rate_limit.remaining);
              }
              setClarificationQuestion(null);
            } else {
              setError(result.error || result.details || 'Не удалось получить ссылку для скачивания.');
              setPendingUpdateBubble(null);
            }
            setLoading(false);
            setIsUpdating(false);
            setCurrentPhase(null);
            setCurrentAgent(null);
            resolve();
          } else if (data.status === 'CLARIFICATION_NEEDED') {
            const result = data.result;
            // Capture server-side session_id / dialogue_id (Q1: clarification
            // responses now carry dialogue_id so the first request binds the
            // dialogue folder; R8 single capture point)
            captureIds(data);

            // Обновляем расшифровку аудио в последнем элементе clarificationHistory
            if (result.user_query) {
              const hist = clarificationHistoryRef.current;
              const lastEntry = hist[hist.length - 1];
              if (lastEntry && lastEntry.audioUrl && !lastEntry.answer) {
                const updated = [...hist];
                updated[updated.length - 1] = { ...lastEntry, answer: result.user_query };
                clarificationHistoryRef.current = updated;
                setClarificationHistory(updated);
              }
            }

            if (result.is_informational) {
              // Информационный ответ о сцене/истории — показываем как системное сообщение
              // БЕЗ инкремента off-topic счётчика, БЕЗ блокировки
              console.log(`[pollTaskStatus] Informational response`);
              if (isUpdate) {
                const pendingAudioInfo = pendingUpdateAudioRef.current;
                setUpdateHistory(prev => [...prev, {
                  query: pendingUpdateQueryRef.current,
                  offTopicResponse: result.clarification_question,
                  isTerminal: false,
                  ...(pendingAudioInfo && {
                    audioUrl: pendingAudioInfo.audioUrl,
                    audio: pendingAudioInfo.audio,
                    transcription: result.user_query || '',
                  }),
                }]);
                setPendingUpdateBubble(null);
                pendingUpdateAudioRef.current = null;
                setClarificationQuestion(null);
                setIsOutOfScope(false);
              } else {
                setClarificationQuestion(result.clarification_question);
                setIsOutOfScope(true);
              }
              // Сброс off-topic счётчика — информационный ответ не является нарушением
              offTopicCountRef.current = 0;
            } else if (result.is_out_of_scope) {
              // Off-topic: увеличиваем счётчик
              offTopicCountRef.current += 1;
              const strikes = offTopicCountRef.current;
              console.log(`[pollTaskStatus] Off-topic strike #${strikes}`);

              if (isUpdate) {
                // Off-topic при обновлении: сохраняем в историю
                const pendingAudioInfo = pendingUpdateAudioRef.current;
                setUpdateHistory(prev => [...prev, {
                  query: pendingUpdateQueryRef.current,
                  offTopicResponse: result.clarification_question,
                  isTerminal: strikes >= 3,
                  ...(pendingAudioInfo && {
                    audioUrl: pendingAudioInfo.audioUrl,
                    audio: pendingAudioInfo.audio,
                    transcription: result.user_query || '',
                  }),
                }]);
                setPendingUpdateBubble(null);
                pendingUpdateAudioRef.current = null;
                setClarificationQuestion(null);
                setIsOutOfScope(false);
              } else {
                // Off-topic при генерации: показываем как системное сообщение + позволяем ввести новый запрос
                setClarificationQuestion(result.clarification_question);
                setIsOutOfScope(true);
              }

              // Блокировка после 3 нарушений подряд
              if (strikes >= 3) {
                setChatLocked(true);
              }
            } else {
              // Обычное уточнение — pendingUpdateBubble НЕ сбрасываем, чтобы запрос оставался видим
              setClarificationQuestion(result.clarification_question);
              setIsOutOfScope(false);
              // Успешное уточнение (не off-topic) — сброс счётчика
              offTopicCountRef.current = 0;
            }
            setLoading(false);
            setIsUpdating(false);
            setCurrentPhase(null);
            setCurrentAgent(null);
            resolve();
          } else if (data.status === 'FAILURE') {
            setError(data.error || 'Произошла ошибка при обработке запроса.');
            setPendingUpdateBubble(null);
            setLoading(false);
            setIsUpdating(false);
            setCurrentPhase(null);
            setCurrentAgent(null);
            resolve();
          } else {
            // PENDING или PROCESSING
            if (data.progress) {
              if (data.progress.phase) setCurrentPhase(data.progress.phase);
              if (data.progress.agent) setCurrentAgent(data.progress.agent);
              // Сервер отдаёт массив мыслей; guard (m2): не трогаем стейт,
              // если длина и последняя запись не изменились
                if (Array.isArray(data.progress.thoughts) && data.progress.thoughts.length > 0) {
                  const next = data.progress.thoughts;
                  const cur = agentThoughtsRef.current;
                  const lastNext = next[next.length - 1];
                  const lastCur = cur[cur.length - 1];
                  const identical = cur.length === next.length
                    && lastCur && lastNext
                    && lastCur.text === lastNext.text
                    && lastCur.label === lastNext.label;
                  if (!identical) setAgentThoughts(next);
                }
            }
            // Продолжаем опрашивать через 2 секунды
            setTimeout(poll, 2000);
          }
        } catch (err) {
          console.error('Polling error:', err);
          setError('Ошибка при проверке статуса задачи.');
          setPendingUpdateBubble(null);
          setLoading(false);
          setIsUpdating(false);
          setCurrentPhase(null);
          setCurrentAgent(null);
          resolve();
        }
      };
      poll();
    });
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    if (!updateQuery && !updateRecorder.audioBlob) {
      setError('Пожалуйста, введите запрос или запишите аудио');
      return;
    }
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    pollCountRef.current = 0;

    setIsUpdating(true);
    setError(null);
    setPendingUpdateBubble({ text: updateQuery, audioUrl: updateRecorder.audioUrl });
    setAgentThoughts([]);

    setIsClarificationForUpdate(true);
    setClarificationHistory([]);
    clarificationHistoryRef.current = [];
    setClarificationQuestion(null);

    try {
      let audioData = null;
      let audioBase64Full = null;
      if (updateRecorder.audioBlob) {
        audioBase64Full = await blobToBase64(updateRecorder.audioBlob);
        audioData = audioBase64Full.split(',')[1];
      }

      // Сохраняем аудио-данные в ref для доступа из pollTaskStatus
      pendingUpdateAudioRef.current = audioData
        ? { audioUrl: updateRecorder.audioUrl, audio: audioData }
        : null;

      pendingUpdateQueryRef.current = updateQuery;

      const conversation_history = buildConversationHistory({
        query, scenePlanningInfo, updateHistory,
        offTopicCount: offTopicCountRef.current,
      });

      const payload = {
        query: updateQuery || undefined,
        file_url: updatedDownloadUrl || downloadUrl,
        preset_path: presetPathRef.current || undefined,
        audio_data: audioData,
        conversation_history,
        session_id: sessionIdRef.current,
        // Q2: keep the dialogue chain intact across updates
        dialogue_id: dialogueIdRef.current || undefined,
      };

      const response = await fetch('/api/v1/update-scene/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
          'X-Auth-Token': apiToken,
        },
        body: JSON.stringify(payload),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Произошла ошибка при обновлении сцены');
      }

      if (data.success && data.task_id) {
        // Capture server session_id from update response (202 Accepted)
        if (data.session_id) sessionIdRef.current = data.session_id;
        // Показываем транскрипцию аудио сразу в pendingUpdateBubble
        if (data.user_query && audioData) {
          setPendingUpdateBubble(prev => ({ ...prev, transcription: data.user_query }));
        }
        // Начинаем поллинг статуса задачи
        await pollTaskStatus(data.task_id, true);
        setUpdateQuery('');
        updateRecorder.deleteRecording();
      } else {
        throw new Error(data.error || 'Неизвестная ошибка');
      }
    } catch (err) {
      console.error('Error updating scene:', err);
      setError(err.message || 'Произошла ошибка при обновлении сцены');
      setIsUpdating(false);
      setPendingUpdateBubble(null);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim() && !initialRecorder.audioBlob) return;
    if (!apiToken.trim()) {
      setError('Пожалуйста, введите API токен');
      return;
    }
    pollCountRef.current = 0;

    // Сохраняем исходный запрос и данные для уточнений
    clarificationOriginalQueryRef.current = query;
    clarificationOriginalPayloadRef.current = {
      body: selectedBody,
      mode: selectedMode,
    };
    setIsClarificationForUpdate(false);
    setClarificationHistory([]);
    clarificationHistoryRef.current = [];
    setClarificationQuestion(null);
    setIsOutOfScope(false);
    setChatLocked(false);
    offTopicCountRef.current = 0;
    sessionIdRef.current = null;
    // F3: dialogueIdRef НЕ сбрасываем — следующая генерация в том же чате
    // переиспользует диалоговую папку; сброс только при новом чате (reload)
    // или загрузке из истории (loadChatFromHistory ставит chat.dialogue_id).
    chatIdRef.current = null;
    presetPathRef.current = null;
    prevSaveKeyRef.current = '';

    setLoading(true);
    setError(null);
    setDownloadUrl(null);
    setInitialGenClarifications([]);
    setUpdateHistory([]);
    setScenePlanningInfo(null);
    setSavedPlanningDetails([]);
    seenPlanningDetailsRef.current = [];
    setCurrentPhase(null);
    setCurrentAgent(null);
    setAgentThoughts([]);
    setSubmittedAudioUrl(initialRecorder.audioBlob ? initialRecorder.audioUrl : null);
    setSubmittedAudioTranscription(null);
    setSubmittedAudioBase64(null);

    try {
      let audioData = null;
      if (initialRecorder.audioBlob) {
        audioData = await blobToBase64(initialRecorder.audioBlob);
        setSubmittedAudioBase64(audioData.split(',')[1]);
      }

      const response = await fetch('/api/v1/pirogov-query/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
          'X-Auth-Token': apiToken,
        },
        body: JSON.stringify({
          query: query || undefined,
          audio_data: audioData,
          body: selectedBody,
          mode: selectedMode,
          session_id: sessionIdRef.current,
          dialogue_id: dialogueIdRef.current || undefined,
        }),
        credentials: 'include',
      });

      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (jsonError) {
        console.error('Server response is not JSON:', text);
        setError(`Ошибка сервера (${response.status}): ${text.substring(0, 200)}`);
        setLoading(false);
        return;
      }

      if (response.status === 401) {
        setError('❌ Неверный токен. Проверьте и попробуйте снова.');
        setShowTokenInput(true);
        setLoading(false);
      } else if (response.status === 429) {
        setError('⚠️ Лимит запросов на месяц исчерпан. Попробуйте в следующем месяце.');
        setLoading(false);
      } else if (response.status === 202 && data.task_id) {
        // Задача принята в обработку, начинаем опрос
        if (data.session_id) sessionIdRef.current = data.session_id;
        // Показываем транскрипцию сразу, не ожидая конца обработки
        if (data.user_query && audioData) setSubmittedAudioTranscription(data.user_query);
        pollTaskStatus(data.task_id, false);
      } else if (response.ok && data.download_url) {
        // Fallback for synchronous response (if any)
        // dialogue_id arrives via captureIds during polling; a sync
        // success would carry it at data.dialogue_id (kept for shape parity)
        if (data.dialogue_id) dialogueIdRef.current = data.dialogue_id;
        setDownloadUrl(data.download_url);
        presetPathRef.current = data.preset_path || null;
        const fullScenePlanningInfo = {
          ...data.scene_planning_info,
          rationale: data.ai_response?.rationale,
          applied_mode: data.ai_response?.applied_mode,
          special_rules: data.ai_response?.special_rules,
        };
        setScenePlanningInfo(fullScenePlanningInfo);
        
        if (data.rate_limit && data.rate_limit.remaining !== undefined) {
          setRequestsRemaining(data.rate_limit.remaining);
        }
        setLoading(false);
      } else {
        setError(data.error || data.details || 'Не удалось получить ссылку для скачивания.');
        setLoading(false);
      }
    } catch (err) {
      console.error('Request error:', err);
      setError('Ошибка подключения к серверу.');
      setLoading(false);
    }
  };

  // НОВОЕ: Отправка ответа на уточняющий вопрос ИИ
  const handleClarificationSubmit = async (e) => {
    e.preventDefault();
    if (!clarificationAnswer.trim() && !clarRecorder.audioBlob) return;
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    pollCountRef.current = 0;

    const answer = clarificationAnswer.trim();
    const question = clarificationQuestion;

    // Конвертируем аудио в base64 если есть
    let audioData = null;
    if (clarRecorder.audioBlob) {
      try {
        const base64Audio = await blobToBase64(clarRecorder.audioBlob);
        audioData = base64Audio.split(',')[1];
      } catch (err) {
        console.error('Audio conversion error:', err);
      }
    }

    // Сохраняем audioUrl перед удалением записи (для воспроизведения в текущей сессии)
    const savedAudioUrl = clarRecorder.audioBlob ? clarRecorder.audioUrl : null;

    const newHistory = [...clarificationHistory, {
      question,
      answer: answer || '',
      ...(audioData && { audio: audioData }),
      ...(savedAudioUrl && { audioUrl: savedAudioUrl }),
    }];
    clarificationHistoryRef.current = newHistory;
    setClarificationHistory(newHistory);
    setClarificationAnswer('');
    setClarificationQuestion(null);
    setIsOutOfScope(false);
    clarRecorder.deleteRecording();

    if (isClarificationForUpdate) {
      // Уточнение для обновления сцены
      setIsUpdating(true);
      setError(null);
      try {
        const conversation_history = buildConversationHistory({
          query, scenePlanningInfo, updateHistory,
          offTopicCount: offTopicCountRef.current,
        });

        const payload = {
          query: pendingUpdateQueryRef.current || updateQuery || undefined,
          file_url: updatedDownloadUrl || downloadUrl,
          preset_path: presetPathRef.current || undefined,
          conversation_history,
          clarification_history: newHistory,
          audio_data: audioData,
          session_id: sessionIdRef.current,
          // Q2: clarification-for-update must stay in the same dialogue
          dialogue_id: dialogueIdRef.current || undefined,
        };

        const response = await fetch('/api/v1/update-scene/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCookie('csrftoken'),
            'X-Auth-Token': apiToken,
          },
          body: JSON.stringify(payload),
          credentials: 'include',
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Ошибка при обновлении сцены');
        if (data.success && data.task_id) {
          await pollTaskStatus(data.task_id, true);
        } else {
          throw new Error(data.error || 'Неизвестная ошибка');
        }
      } catch (err) {
        console.error('Clarification update error:', err);
        setError(err.message || 'Ошибка при отправке уточнения');
        setIsUpdating(false);
      } finally {
        isSubmittingRef.current = false;
      }
    } else {
      // Уточнение для генерации
      setLoading(true);
      setError(null);
      try {
        const payload = {
          query: clarificationOriginalQueryRef.current || query || undefined,
          body: clarificationOriginalPayloadRef.current?.body || selectedBody,
          mode: clarificationOriginalPayloadRef.current?.mode || selectedMode,
          clarification_history: newHistory,
          audio_data: audioData,
          session_id: sessionIdRef.current,
          dialogue_id: dialogueIdRef.current || undefined,
        };

        const response = await fetch('/api/v1/pirogov-query/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCookie('csrftoken'),
            'X-Auth-Token': apiToken,
          },
          body: JSON.stringify(payload),
          credentials: 'include',
        });

        const data = await response.json();
        if (response.status === 202 && data.task_id) {
          if (data.session_id) sessionIdRef.current = data.session_id;
          pollTaskStatus(data.task_id, false);
        } else if (response.ok && data.download_url) {
          // Sync fallback (never used today — all responses are 202 + poll)
          if (data.dialogue_id) dialogueIdRef.current = data.dialogue_id;
          setDownloadUrl(data.download_url);
          setLoading(false);
        } else {
          setError(data.error || 'Ошибка при обработке запроса');
          setLoading(false);
        }
      } catch (err) {
        console.error('Clarification generation error:', err);
        setError(err.message || 'Ошибка при отправке уточнения');
        setLoading(false);
      } finally {
        isSubmittingRef.current = false;
      }
    }
  };

  // ── История чатов: сериализация / десериализация / API ──

  const serializeChatState = () => {
    const messages = [];
    messages.push({
      role: 'user', type: 'query', text: query,
      ...(submittedAudioBase64 && { audio: submittedAudioBase64 }),
      ...(submittedAudioTranscription && { transcription: submittedAudioTranscription }),
    });

    initialGenClarifications.forEach(c => {
      messages.push({ role: 'assistant', type: 'clarification', text: c.question });
      messages.push({
        role: 'user', type: 'clarification_answer', text: c.answer,
        ...(c.audio && { audio: c.audio }),
      });
    });

    if (downloadUrl) {
      messages.push({
        role: 'assistant', type: 'generation',
        download_url: downloadUrl,
        scene_planning_info: scenePlanningInfo,
        saved_planning_details: savedPlanningDetails,
        raw_preset_filename: rawPresetRef.current || null,
      });
    }

    updateHistory.forEach(item => {
      messages.push({
        role: 'user', type: 'scene_edit_query', text: item.query,
        ...(item.audio && { audio: item.audio }),
        ...(item.transcription && { transcription: item.transcription }),
      });
      if (item.clarificationRounds?.length) {
        item.clarificationRounds.forEach(c => {
          messages.push({ role: 'assistant', type: 'clarification', text: c.question });
          messages.push({
            role: 'user', type: 'clarification_answer', text: c.answer,
            ...(c.audio && { audio: c.audio }),
          });
        });
      }
      if (item.offTopicResponse) {
        messages.push({
          role: 'assistant', type: 'off_topic',
          text: item.offTopicResponse, is_terminal: item.isTerminal || false,
        });
      } else {
        messages.push({
          role: 'assistant', type: 'scene_edit',
          download_url: item.url,
          saved_thoughts: item.savedThoughts || [],
          completion_report: item.completionReport || null,
          scene_generated: item.sceneGenerated || false,
          scene_planning_info: item.scenePlanningInfo || null,
          rationale: item.rationale || null,
          raw_preset_filename: item.rawPresetFilename || null,
        });
      }
    });

    return messages;
  };

  // Восстановить blob URL из base64 аудио (для воспроизведения в плеере)
  const base64AudioToUrl = (base64) => {
    try {
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'audio/wav' });
      return URL.createObjectURL(blob);
    } catch { return null; }
  };

  const deserializeChatMessages = (messages) => {
    let restoredQuery = '';
    let restoredAudioUrl = null;
    let restoredAudioBase64 = null;
    let restoredAudioTranscription = null;
    let restoredDownloadUrl = null;
    let restoredScenePlanningInfo = null;
    let restoredSavedPlanningDetails = [];
    let restoredRawPresetFilename = null;
    let restoredInitialGenClarifications = [];
    const restoredUpdateHistory = [];
    let currentClarifications = [];
    let currentUpdateQuery = '';
    let currentUpdateAudio = null;
    let currentUpdateTranscription = null;

    for (const msg of messages) {
      if (msg.type === 'query' && msg.role === 'user') {
        restoredQuery = msg.text;
        if (msg.audio) {
          restoredAudioBase64 = msg.audio;
          restoredAudioUrl = base64AudioToUrl(msg.audio);
          restoredAudioTranscription = msg.transcription || null;
        }
      } else if (msg.type === 'clarification' && msg.role === 'assistant') {
        currentClarifications.push({ question: msg.text, answer: '' });
      } else if (msg.type === 'clarification_answer' && msg.role === 'user') {
        if (currentClarifications.length) {
          const entry = { answer: msg.text };
          if (msg.audio) {
            entry.audio = msg.audio;
            entry.audioUrl = base64AudioToUrl(msg.audio);
          }
          Object.assign(currentClarifications[currentClarifications.length - 1], entry);
        }
      } else if (msg.type === 'generation' && msg.role === 'assistant') {
        restoredDownloadUrl = msg.download_url;
        restoredScenePlanningInfo = msg.scene_planning_info;
        restoredSavedPlanningDetails = msg.saved_planning_details || [];
        restoredRawPresetFilename = msg.raw_preset_filename || null;
        restoredInitialGenClarifications = [...currentClarifications];
        currentClarifications = [];
      } else if (msg.type === 'scene_edit_query' && msg.role === 'user') {
        currentUpdateQuery = msg.text;
        currentUpdateAudio = msg.audio || null;
        currentUpdateTranscription = msg.transcription || null;
        currentClarifications = [];
      } else if (msg.type === 'off_topic' && msg.role === 'assistant') {
        restoredUpdateHistory.push({
          query: currentUpdateQuery,
          offTopicResponse: msg.text,
          isTerminal: msg.is_terminal || false,
          ...(currentUpdateAudio && {
            audio: currentUpdateAudio,
            audioUrl: base64AudioToUrl(currentUpdateAudio),
            transcription: currentUpdateTranscription,
          }),
        });
        currentClarifications = [];
      } else if (msg.type === 'scene_edit' && msg.role === 'assistant') {
        restoredUpdateHistory.push({
          query: currentUpdateQuery,
          url: msg.download_url,
          savedThoughts: msg.saved_thoughts || [],
          completionReport: msg.completion_report || null,
          clarificationRounds: [...currentClarifications],
          sceneGenerated: msg.scene_generated || false,
          scenePlanningInfo: msg.scene_planning_info || null,
          rationale: msg.rationale || null,
          rawPresetFilename: msg.raw_preset_filename || null,
          ...(currentUpdateAudio && {
            audio: currentUpdateAudio,
            audioUrl: base64AudioToUrl(currentUpdateAudio),
            transcription: currentUpdateTranscription,
          }),
        });
        currentClarifications = [];
      }
    }

    return {
      query: restoredQuery,
      audioUrl: restoredAudioUrl,
      audioBase64: restoredAudioBase64,
      audioTranscription: restoredAudioTranscription,
      downloadUrl: restoredDownloadUrl,
      scenePlanningInfo: restoredScenePlanningInfo,
      savedPlanningDetails: restoredSavedPlanningDetails,
      rawPresetFilename: restoredRawPresetFilename,
      initialGenClarifications: restoredInitialGenClarifications,
      updateHistory: restoredUpdateHistory,
    };
  };

  const saveChat = async () => {
    if (!apiToken || !downloadUrl) return;
    try {
      const messages = serializeChatState();
      const resp = await fetch('/api/v1/chats/save/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
          'X-Auth-Token': apiToken,
        },
        body: JSON.stringify({
          // dialogue_id как chat_id: артефакты уже лежат в папке диалога (бэкенд пропускает raw-copy);
          // dialogue_id — источник истины (новейшая папка диалога), chatIdRef — только фолбэк
          chat_id: dialogueIdRef.current || chatIdRef.current || undefined,
          body: selectedBody,
          mode: selectedMode,
          title: (query || submittedAudioTranscription || '').substring(0, 120),
          // Q2: persist the dialogue id at chat level so loadChatFromHistory
          // can restore the dialogue chain
          dialogue_id: dialogueIdRef.current || undefined,
          messages,
        }),
        credentials: 'include',
      });
      const data = await resp.json();
      if (data.success && data.chat_id) {
        chatIdRef.current = data.chat_id;
        console.log('✓ Chat saved:', data.chat_id);
      }
    } catch (err) {
      console.error('Auto-save failed:', err);
    }
  };

  const fetchChatList = async () => {
    if (!apiToken) return;
    try {
      setLoadingHistory(true);
      const resp = await fetch(`/api/v1/chats/?body=${encodeURIComponent(selectedBody)}`, {
        headers: { 'X-CSRFToken': getCookie('csrftoken'), 'X-Auth-Token': apiToken },
        credentials: 'include',
      });
      const data = await resp.json();
      if (data.success) setChatList(data.chats || []);
    } catch (err) {
      console.error('Failed to fetch chat list:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadChatFromHistory = async (chatId) => {
    try {
      setLoadingHistory(true);
      const resp = await fetch(`/api/v1/chats/${encodeURIComponent(chatId)}/?body=${encodeURIComponent(selectedBody)}`, {
        headers: { 'X-CSRFToken': getCookie('csrftoken'), 'X-Auth-Token': apiToken },
        credentials: 'include',
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Failed to load chat');

      const chat = data.chat;
      const state = deserializeChatMessages(chat.messages || []);

      setQuery(state.query);
      setDownloadUrl(state.downloadUrl);
      setScenePlanningInfo(state.scenePlanningInfo);
      setSavedPlanningDetails(state.savedPlanningDetails);
      seenPlanningDetailsRef.current = state.savedPlanningDetails || [];
      setInitialGenClarifications(state.initialGenClarifications);
      setUpdateHistory(state.updateHistory);
      chatIdRef.current = chat.chat_id;
      // Q2: restore the dialogue chain saved by chats/save
      dialogueIdRef.current = chat.dialogue_id || null;
      rawPresetRef.current = state.rawPresetFilename || null;

      // Восстанавливаем аудио из истории
      setSubmittedAudioUrl(state.audioUrl || null);
      setSubmittedAudioBase64(state.audioBase64 || null);
      setSubmittedAudioTranscription(state.audioTranscription || null);

      // Set the latest download URL for future updates
      if (state.updateHistory.length > 0) {
        const lastWithUrl = [...state.updateHistory].reverse().find(h => h.url);
        if (lastWithUrl) setUpdatedDownloadUrl(lastWithUrl.url);
      } else {
        setUpdatedDownloadUrl(null);
      }

      // Clear transient state
      setLoading(false);
      setIsUpdating(false);
      setError(null);
      setClarificationQuestion(null);
      setClarificationHistory([]);
      setClarificationAnswer('');
      setChatLocked(false);
      setPendingUpdateBubble(null);
      setAgentThoughts([]);
      setCurrentPhase(null);
      setCurrentAgent(null);
      sessionIdRef.current = null;
      // dialogueIdRef was already restored from chat.dialogue_id above (Q2)
      presetPathRef.current = null;
      offTopicCountRef.current = 0;

      setShowHistoryModal(false);
    } catch (err) {
      console.error('Failed to load chat:', err);
      setError('Ошибка при загрузке чата из истории');
    } finally {
      setLoadingHistory(false);
    }
  };

  const deleteChatFromHistory = async (chatId) => {
    try {
      const resp = await fetch(`/api/v1/chats/${encodeURIComponent(chatId)}/delete/?body=${encodeURIComponent(selectedBody)}`, {
        method: 'DELETE',
        headers: { 'X-CSRFToken': getCookie('csrftoken'), 'X-Auth-Token': apiToken },
        credentials: 'include',
      });
      const data = await resp.json();
      if (data.success) {
        setChatList(prev => prev.filter(c => c.chat_id !== chatId));
        if (chatIdRef.current === chatId) chatIdRef.current = null;
      }
    } catch (err) {
      console.error('Failed to delete chat:', err);
    }
  };

  const formatChatDate = (ts) => {
    if (!ts) return '';
    // ts format: "YYYYMMDD_HHMMSS"
    const m = ts.match(/^(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})$/);
    if (!m) return ts;
    return `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]}`;
  };

  // ── Авто-сохранение чата после изменений ──
  const prevSaveKeyRef = useRef('');
  useEffect(() => {
    if (!downloadUrl || !apiToken || loading || isUpdating) return;
    const saveKey = `${downloadUrl}|${updateHistory.length}`;
    if (saveKey === prevSaveKeyRef.current) return;
    prevSaveKeyRef.current = saveKey;
    const timer = setTimeout(() => saveChat(), 800);
    return () => clearTimeout(timer);
  }, [downloadUrl, updateHistory, loading, isUpdating]);

  // Keyframes и основные стили загружаются из pirogov-dev.css

  // Helper: read scene field, fallback to rationale.* when available
  const getSceneValue = (key) => {
    if (!scenePlanningInfo) return undefined;
    if (scenePlanningInfo[key]) return scenePlanningInfo[key];
    if (scenePlanningInfo.rationale && typeof scenePlanningInfo.rationale !== 'string') {
      return scenePlanningInfo.rationale[key];
    }
    return undefined;
  };

  const visualizationIntent = getSceneValue('visualization_intent');
  const focusChoice = getSceneValue('focus_choice');
  const contextChoice = getSceneValue('context_choice');
  const viewRecommendation = getSceneValue('view_recommendation');

  return (
    <>

      {/* Instructions Modal */}
      {showInstructions && (
        <InstructionsModal
          selectedMode={selectedMode}
          onSelectMode={setSelectedMode}
          currentInstruction={currentInstruction}
          onClose={() => setShowInstructions(false)}
        />
      )}

      {/* Body Switch Confirm Modal */}
      {showBodySwitchConfirm && (
        <BodySwitchModal
          selectedBody={selectedBody}
          onClose={() => setShowBodySwitchConfirm(false)}
        />
      )}

      {/* History Modal */}
      {showHistoryModal && (
        <div className="pd-overlay" onClick={() => setShowHistoryModal(false)}>
          <div className="pd-modal pd-modal--history" onClick={(e) => e.stopPropagation()}>
            <div className="pd-modal__header">
              <span className="pd-modal__title"><img src={historyIcon} alt="" className="pd-btn-icon history" style={{marginRight: 6}} /> История чатов</span>
              <button className="pd-modal__close" onClick={() => setShowHistoryModal(false)}>✕</button>
            </div>
            <div className="pd-modal__body">
              {loadingHistory ? (
                <div className="pd-history-empty">Загрузка...</div>
              ) : chatList.length === 0 ? (
                <div className="pd-history-empty">Нет сохранённых чатов</div>
              ) : (
                <div className="pd-history-list">
                  {chatList.map(chat => (
                    <div
                      key={chat.chat_id}
                      className={`pd-history-item${chatIdRef.current === chat.chat_id ? ' pd-history-item--active' : ''}`}
                    >
                      <div
                        className="pd-history-item__body"
                        onClick={() => loadChatFromHistory(chat.chat_id)}
                      >
                        <div className="pd-history-item__title">{chat.title || 'Без названия'}</div>
                        <div className="pd-history-item__meta">
                          <span><img src={chatIcon} alt="" className="pd-meta-icon" />{chat.message_count}</span>
                          <span><img src={fileIcon} alt="" className="pd-meta-icon" />{chat.scene_count}</span>
                          <span>{formatChatDate(chat.updated_at)}</span>
                        </div>
                      </div>
                      <button
                        className="pd-history-item__delete"
                        onClick={(e) => { e.stopPropagation(); deleteChatFromHistory(chat.chat_id); }}
                        title="Удалить чат"
                      >
                        <img src={trashIcon} alt="удалить" className="pd-btn-icon" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className={`pd-root${downloadUrl ? ' pd-root--chat' : ' pd-root--form'}`}>

      <div 
        className={styles.backgroundSvg}
      />
        
        {/* Декоративные круги на фоне */}
        <div className="pd-deco-circle pd-deco-circle--top" />
        <div className="pd-deco-circle pd-deco-circle--bottom" />



        {/* Основной контейнер */}
        <div className="pd-container">
          {/* Логотип — убран */}

          <div className="pd-header">
            <div className="pd-header__row">
              <h1 className="pd-header__title"><a href="/" style={{ color: 'inherit', textDecoration: 'none' }}>🧠 Brain AI</a></h1>

              {/* ── Кнопка мобильного меню ── */}
              <button
                type="button"
                className="pd-menu-btn"
                onClick={() => setShowMobileMenu(prev => !prev)}
                aria-label="Меню"
              >
                <img src={menuIcon} alt="меню" className="pd-btn-icon" />
              </button>

              {/* ── Навигация шапки ── */}
              <div className={`pd-header-nav${showMobileMenu ? ' pd-header-nav--open' : ''}`}>

                {/* ── Кнопка «Новый чат» ── */}
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="pd-header-btn"
                >
                  <img src={plusIcon} alt="" className="pd-btn-icon" />
                  <span className="pd-btn-label">Новый чат</span>
                </button>

                {/* ── Кнопка «История» ── */}
                <button
                  type="button"
                  onClick={() => { fetchChatList(); setShowHistoryModal(true); }}
                  className="pd-header-btn pd-header-btn--history"
                  disabled={!apiToken}
                >
                  <img src={historyIcon} alt="" className="pd-btn-icon" />
                  <span className="pd-btn-label">История</span>
                </button>

                {/* ── Кнопка ключа (в меню) ── */}
                {apiToken && (
                  <button
                    type="button"
                    onClick={() => setShowTokenInput(prev => !prev)}
                    className="pd-token-btn"
                    title="API токен"
                  >
                    🔑
                  </button>
                )}

                {/* ── Триггер Мужчина / Женщина ── */}
                <div className="pd-body-toggle">
                  {bodyOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        if (opt.value !== selectedBody) {
                          if (isInChatMode) {
                            setShowBodySwitchConfirm(true);
                          } else {
                            setSelectedBody(opt.value);
                          }
                        }
                      }}
                      className={`pd-body-toggle__btn${selectedBody === opt.value ? ' pd-body-toggle__btn--active' : ' pd-body-toggle__btn--inactive'}`}
                    >
                      <span className="pd-btn-label">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              {/* ── Оставшиеся запросы — всегда видны (без кнопки) ── */}
              <div className="pd-header__right">
                {apiToken && requestsRemaining !== null && (
                  <div className="pd-requests-badge">
                    <span className="pd-requests-badge__label">Запросов осталось:</span>
                    <span className="pd-requests-badge__count">{requestsRemaining}</span>
                  </div>
                )}
              </div>
            </div>
            {/*<Link to="/" className={styles.backBtn}>
              ← Назад в Меню
            </Link>*/}

            {/* 🔑 Плашка для ввода/замены токена */}
            {showTokenInput && (
              <div className="pd-token-panel">
                <div className="pd-token-panel__header">
                  <span className="pd-token-panel__icon">🔑</span>
                  <div className="pd-token-panel__title">
                    API Токен {apiToken ? '(замена)' : '(обязателен)'}
                  </div>
                </div>
                <input
                  type="password"
                  value={apiToken}
                  onChange={(e) => setApiToken(e.target.value)}
                  placeholder="Вставьте ваш API токен здесь..."
                  className="pd-token-input"
                />
                <div className="pd-token-panel__actions">
                  {apiToken && (
                    <button
                      type="button"
                      onClick={() => {
                        handleTokenSave(apiToken);
                      }}
                      className="pd-token-save"
                    >
                      ✓ Сохранить
                    </button>
                  )}
                  {apiToken && (
                    <button
                      type="button"
                      onClick={handleTokenReset}
                      className="pd-token-delete"
                    >
                      <img src={trashIcon} alt="trash" style={{ width: 16, height: 16 }} /> Удалить
                    </button>
                  )}
                  {!apiToken && (
                    <button
                      type="button"
                      onClick={() => {
                        if (!apiToken.trim()) {
                          setError('Пожалуйста, введите токен');
                          return;
                        }
                        handleTokenSave(apiToken);
                      }}
                      className="pd-token-apply"
                    >
                      ✓ Применить токен
                    </button>
                  )}
                </div>
                <div className="pd-token-panel__hint">
                  💡 Токен будет сохранён в cookies браузера и не потребуется вводить заново.
                </div>
              </div>
            )}



          </div>

          {!downloadUrl && !loading && !clarificationQuestion && clarificationHistory.length === 0 && (
          <div className="pd-form-center">
          <div className="pd-form-inner">

            <WelcomeMessage />

            <form onSubmit={handleSubmit}>

              <div className="pd-form__heading">
                Что показать?
              </div>

              <div className="pd-form__subheading">
                Удерживайте кнопку Пробел ( ␣ ) для записи голоса или опишите текстом, что вы хотите увидеть.
              </div>

              {/* Query Textarea */}
              <div className="pd-form__textarea-wrap">
                
                <div className="pd-form__textarea-relative">
                  {!initialRecorder.audioBlob ? (
                    <>
                      <textarea
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="например, внутренняя анатомия мозга"
                        rows={3}
                        disabled={!!downloadUrl}
                        className="pd-initial-textarea"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e); }
                          if (e.key === ' ' && !query && !initialRecorder.audioBlob && !downloadUrl) { e.preventDefault(); initialRecorder.startRecording(e); }
                        }}
                        onKeyUp={(e) => {
                          if (e.key === ' ' && initialRecorder.recordingRef.current) { initialRecorder.stopRecording(e); }
                        }}
                      />
                      <div className="pd-initial-buttons">
                        <button
                          type="button"
                          disabled={!!downloadUrl}
                          onMouseDown={initialRecorder.startRecording}
                          onMouseUp={initialRecorder.stopRecording}
                          onMouseLeave={initialRecorder.stopRecording}
                          onTouchStart={initialRecorder.startRecording}
                          onTouchEnd={initialRecorder.stopRecording}
                          onTouchCancel={initialRecorder.stopRecording}
                          onContextMenu={(e) => e.preventDefault()}
                          className={`pd-initial-mic${initialRecorder.isRecording ? ' pd-initial-mic--recording' : ''}`}
                          title="Удерживайте для записи голосового сообщения"
                        >
                          <img src={microphoneIcon} alt="mic" className={`pd-initial-mic__icon${initialRecorder.isRecording ? ' pd-initial-mic__icon--recording' : ''}`} />
                        </button>
                        <button
                          type="submit"
                          disabled={loading || !apiToken || !!downloadUrl}
                          className="pd-initial-send"
                          title={!apiToken ? 'Сначала введите токен' : 'Сгенерировать'}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="pd-initial-audio-preview">
                      <WaveformPlayer src={initialRecorder.audioUrl} />
                      <div className="pd-initial-audio-preview__actions">
                        <button
                          type="button"
                          onClick={initialRecorder.deleteRecording}
                          className="pd-audio-delete-btn"
                          title="Удалить запись"
                        >
                          <img src={trashIcon} alt="trash" />
                        </button>
                        <button
                          type="submit"
                          disabled={loading || !apiToken || !!downloadUrl}
                          className="pd-initial-send"
                          title={!apiToken ? 'Сначала введите токен' : 'Сгенерировать'}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

              </div>



              {/* Toggle: Разделить цветами целевые структуры — removed, colors always enabled */}


            </form>

          </div>
          </div>
          )}

          {/* Results Section - Chat Mode */}
          {(downloadUrl || loading || clarificationQuestion || clarificationHistory.length > 0 || initialGenClarifications.length > 0) && (
            <div className="pd-results">

              {/* ── Messages area ── */}
              <div className={`pd-messages${isInChatMode ? ' pd-messages--with-input' : ''}`}>

                {/* Initial generation message */}
                <div className="pd-msg-group">
                  <UserBubble>
                    {submittedAudioUrl
                      ? <>
                          <WaveformPlayer src={submittedAudioUrl} className="pd-submitted-audio" />
                          {submittedAudioTranscription && (
                            <div className="pd-audio-transcription">{submittedAudioTranscription}</div>
                          )}
                        </>
                      : query}
                  </UserBubble>

                  {/* Уточняющие вопросы + ответы первоначальной генерации — до результата */}
                  {initialGenClarifications.length > 0 && (
                    <div className="pd-msg-group">
                      {initialGenClarifications.map((entry, idx) => (
                        <React.Fragment key={`igc-${idx}`}>
                          <AiBubble text={entry.question} animate={false} />
                          <UserBubble>
                            {entry.audioUrl
                              ? <>
                                  <WaveformPlayer src={entry.audioUrl} className="pd-submitted-audio" />
                                  {entry.answer && <div className="pd-audio-transcription">{entry.answer}</div>}
                                </>
                              : entry.answer}
                          </UserBubble>
                        </React.Fragment>
                      ))}
                    </div>
                  )}

                  {downloadUrl && (
                  <div className="pd-scene-response">

                    {savedPlanningDetails.length > 0 && (
                      <div className="pd-planning">
                        <div className="pd-planning__header" onClick={() => setShowPlanningDetails(!showPlanningDetails)}>
                          <span>Детали планирования сцены</span>
                          <span className={`pd-planning__chevron${showPlanningDetails ? ' pd-planning__chevron--open' : ''}`}>▾</span>
                        </div>
                        {showPlanningDetails && (
                          <div className="pd-planning__body">
                            {savedPlanningDetails.map((t, idx) => (
                              <div key={idx} className="pd-planning__section">
                                <div className="pd-planning__label">{t.label}</div>
                                <div className="pd-planning__value">{t.text}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="pd-status-msg">✅ Сцена сгенерирована</div>

                    <DownloadBtn onClick={() => handleDownload(downloadUrl)} initial />
                  </div>
                  )}
                </div>

                {/* Update history messages */}
                {updateHistory.map((item, idx) => {
                  const isLatest = idx === updateHistory.length - 1;
                  return (
                  <div key={idx} className="pd-msg-group pd-msg-group--animated">
                    <UserBubble>
                      {item.audioUrl
                        ? <>
                            <WaveformPlayer src={item.audioUrl} className="pd-submitted-audio" />
                            {(item.transcription || item.query) && <div className="pd-audio-transcription">{item.transcription || item.query}</div>}
                          </>
                        : item.query}
                    </UserBubble>

                    {/* Уточняющие вопросы данного обновления — до результата */}
                    {item.clarificationRounds?.length > 0 && (
                      <div className="pd-msg-group">
                        {item.clarificationRounds.map((entry, cidx) => (
                          <React.Fragment key={`ur-${idx}-${cidx}`}>
                            <AiBubble text={entry.question} animate={false} />
                            <UserBubble>
                              {entry.audioUrl
                                ? <>
                                    <WaveformPlayer src={entry.audioUrl} className="pd-submitted-audio" />
                                    {entry.answer && <div className="pd-audio-transcription">{entry.answer}</div>}
                                  </>
                                : entry.answer}
                            </UserBubble>
                          </React.Fragment>
                        ))}
                      </div>
                    )}

                    {/* Off-topic / informational response */}
                    {item.offTopicResponse && (
                      <AiBubble text={item.offTopicResponse} animate={isLatest} />
                    )}

                    {/* AI response + download */}
                    {!item.offTopicResponse && (
                      <div className="pd-scene-response">
                        {item.savedThoughts?.length > 0 && (
                          <div className="pd-planning">
                            <div className="pd-planning__header" onClick={() => setShowPlanningDetails(!showPlanningDetails)}>
                              <span>Детали обновления сцены</span>
                              <span className={`pd-planning__chevron${showPlanningDetails ? ' pd-planning__chevron--open' : ''}`}>▾</span>
                            </div>
                            {showPlanningDetails && (
                              <div className="pd-planning__body">
                                {item.savedThoughts.map((t, si) => (
                                  <div key={si} className="pd-planning__section">
                                    <div className="pd-planning__label">{t.label}</div>
                                    <div className="pd-planning__value">{t.text}</div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        {item.rationale && !item.savedThoughts?.length && (
                          <AiThought text={item.rationale} animate={isLatest} teal />
                        )}
                        {item.completionReport && (item.completionReport.done?.length > 0 || item.completionReport.failed?.length > 0) && (
                          <div className="pd-completion-report">
                            {item.completionReport.done?.length > 0 && (
                              <div className={item.completionReport.failed?.length > 0 ? 'pd-report-done-group' : undefined}>
                                {item.completionReport.done.map((line, i) => (
                                  <div key={i} className="pd-report-done">✓ {line}</div>
                                ))}
                              </div>
                            )}
                            {item.completionReport.failed?.length > 0 && (
                              <div>
                                {item.completionReport.failed.map((line, i) => (
                                  <div key={i} className="pd-report-failed">✗ {line}</div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Planning details для новой сцены, сгенерированной из update */}
                        {item.sceneGenerated && item.scenePlanningInfo && Object.keys(item.scenePlanningInfo).length > 0 && (
                          <div className="pd-planning">
                            <div className="pd-planning__header" onClick={() => setShowPlanningDetails(!showPlanningDetails)}>
                              <span>Детали планирования сцены</span>
                              <span className={`pd-planning__chevron${showPlanningDetails ? ' pd-planning__chevron--open' : ''}`}>▾</span>
                            </div>
                            {showPlanningDetails && (
                              <div className="pd-planning__body">
                                {item.scenePlanningInfo.rationale && (
                                  <div className="pd-planning__section">
                                    <div className="pd-planning__label">🎓 Цель исследования</div>
                                    <div className="pd-planning__value">
                                      {typeof item.scenePlanningInfo.rationale === 'string' ? item.scenePlanningInfo.rationale : item.scenePlanningInfo.rationale?.name}
                                    </div>
                                  </div>
                                )}
                                {item.scenePlanningInfo.applied_mode && (
                                  <div className="pd-planning__section">
                                    <span className="pd-planning__label">⚙️ Режим: </span>
                                    <span className="pd-planning__mode-value">{item.scenePlanningInfo.applied_mode}</span>
                                  </div>
                                )}
                                {item.scenePlanningInfo.special_rules && (
                                  <div className="pd-planning__section">
                                    <div className="pd-planning__label">⚡ Специальные правила</div>
                                    <div className="pd-planning__value">
                                      {typeof item.scenePlanningInfo.special_rules === 'string' ? item.scenePlanningInfo.special_rules : JSON.stringify(item.scenePlanningInfo.special_rules, null, 2)}
                                    </div>
                                  </div>
                                )}
                                {(item.scenePlanningInfo.visualization_intent || (item.scenePlanningInfo.rationale && typeof item.scenePlanningInfo.rationale !== 'string' && item.scenePlanningInfo.rationale.visualization_intent)) && (
                                  <div className="pd-planning__section">
                                    <div className="pd-planning__label">🔬 Намерение визуализации</div>
                                    <div className="pd-planning__value">{item.scenePlanningInfo.visualization_intent || item.scenePlanningInfo.rationale?.visualization_intent}</div>
                                  </div>
                                )}
                                {(item.scenePlanningInfo.focus_choice || (item.scenePlanningInfo.rationale && typeof item.scenePlanningInfo.rationale !== 'string' && item.scenePlanningInfo.rationale.focus_choice)) && (
                                  <div className="pd-planning__section">
                                    <div className="pd-planning__label">🎯 Целевые структуры</div>
                                    <div className="pd-planning__value">{item.scenePlanningInfo.focus_choice || item.scenePlanningInfo.rationale?.focus_choice}</div>
                                  </div>
                                )}
                                {(item.scenePlanningInfo.context_choice || (item.scenePlanningInfo.rationale && typeof item.scenePlanningInfo.rationale !== 'string' && item.scenePlanningInfo.rationale.context_choice)) && (
                                  <div className="pd-planning__section">
                                    <div className="pd-planning__label">📦 Контекстные структуры</div>
                                    <div className="pd-planning__value">{item.scenePlanningInfo.context_choice || item.scenePlanningInfo.rationale?.context_choice}</div>
                                  </div>
                                )}
                                {(item.scenePlanningInfo.view_recommendation || (item.scenePlanningInfo.rationale && typeof item.scenePlanningInfo.rationale !== 'string' && item.scenePlanningInfo.rationale.view_recommendation)) && (
                                  <div className="pd-planning__section">
                                    <div className="pd-planning__label">👁️ Рекомендации для демонстрации</div>
                                    <div className="pd-planning__value">{item.scenePlanningInfo.view_recommendation || item.scenePlanningInfo.rationale?.view_recommendation}</div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        <div className="pd-status-msg">
                          {item.sceneGenerated ? '+ Новая сцена сгенерирована' : '✅ Сцена обновлена'}
                        </div>

                        <DownloadBtn onClick={() => handleDownload(item.url)} />
                      </div>
                    )}
                  </div>
                  );
                })}

                {/* Пузырь «Думает...» — появляется пока идёт обработка */}

                {/* Ожидающее сообщение пользователя при редактировании */}
                {pendingUpdateBubble && (
                  <UserBubble>
                    {pendingUpdateBubble.audioUrl
                      ? <>
                          <WaveformPlayer src={pendingUpdateBubble.audioUrl} className="pd-submitted-audio" />
                          {pendingUpdateBubble.transcription && (
                            <div className="pd-audio-transcription">{pendingUpdateBubble.transcription}</div>
                          )}
                        </>
                      : pendingUpdateBubble.text}
                  </UserBubble>
                )}

                {/* История уточняющих вопросов — отображается ДО «Думает...» */}
                {clarificationHistory.length > 0 && (
                  <div className="pd-msg-group">
                    {clarificationHistory.map((entry, idx) => (
                      <React.Fragment key={`clarification-${idx}`}>
                        <AiBubble text={entry.question} animate={false} />
                        <UserBubble>
                          {entry.audioUrl
                            ? <>
                                <WaveformPlayer src={entry.audioUrl} className="pd-submitted-audio" />
                                {entry.answer && <div className="pd-audio-transcription">{entry.answer}</div>}
                              </>
                            : entry.answer}
                        </UserBubble>
                      </React.Fragment>
                    ))}
                  </div>
                )}

                {/* Активный уточняющий вопрос от ИИ */}
                {clarificationQuestion && !loading && !isUpdating && (
                  <AiBubble text={clarificationQuestion} animate variant="pop" />
                )}

                {/* Мысли ИИ: сворачиваемая панель + индикатор фазы (T4 streaming-ai-thoughts) */}
                {(loading || isUpdating) && (
                  <>
                    <AiThoughtsPanel thoughts={agentThoughts} isProcessing={isProcessing} />
                    <PhaseIndicator
                      isProcessing={isProcessing}
                      currentPhase={currentPhase}
                    />
                  </>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* ── Fixed bottom input — виден при загрузке (disabled) и при готовой сцене ── */}
              {(downloadUrl || loading) && !clarificationQuestion && !chatLocked && <div className={`pd-fixed-bottom${loading ? ' pd-fixed-bottom--loading' : ''}`}>
                <ChatInputForm
                  value={updateQuery}
                  onChange={(e) => setUpdateQuery(e.target.value)}
                  onSubmit={handleUpdateSubmit}
                  placeholder="Напишите запрос на редактирование..."
                  disabled={isUpdating || loading}
                  isRecording={updateRecorder.isRecording}
                  recordingRef={updateRecorder.recordingRef}
                  onStartRecording={updateRecorder.startRecording}
                  onStopRecording={updateRecorder.stopRecording}
                  audioBlob={updateRecorder.audioBlob}
                  audioUrl={updateRecorder.audioUrl}
                  onDeleteRecording={updateRecorder.deleteRecording}
                  submitDisabled={loading || isUpdating || (!updateQuery && !updateRecorder.audioBlob)}
                  submitTitle={isUpdating ? 'Обновление...' : 'Редактировать'}
                />
              </div>}

              {/* ── Панель «Чат заблокирован» — только после 3 off-topic нарушений ── */}
              {chatLocked && !loading && !isUpdating && <div className="pd-fixed-bottom">
                <div className="pd-locked-msg">
                  🔒 Чат заблокирован из-за повторных нарушений правил использования.
                </div>
                <div className="pd-locked-actions">
                  <button
                    type="button"
                    onClick={() => window.location.reload()}
                    className="pd-locked-btn"
                  >↻ Новый чат</button>
                </div>
              </div>}

              {/* ── Фиксированная панель ввода ответа на уточняющий вопрос ── */}
              {clarificationQuestion && !loading && !isUpdating && !chatLocked && <div className="pd-fixed-bottom">
                <ChatInputForm
                  value={clarificationAnswer}
                  onChange={(e) => setClarificationAnswer(e.target.value)}
                  onSubmit={handleClarificationSubmit}
                  placeholder={isOutOfScope ? 'Введите запрос об анатомии человека...' : 'Ваш ответ на уточняющий вопрос ИИ...'}
                  disabled={false}
                  isRecording={clarRecorder.isRecording}
                  recordingRef={clarRecorder.recordingRef}
                  onStartRecording={clarRecorder.startRecording}
                  onStopRecording={clarRecorder.stopRecording}
                  audioBlob={clarRecorder.audioBlob}
                  audioUrl={clarRecorder.audioUrl}
                  onDeleteRecording={clarRecorder.deleteRecording}
                  submitDisabled={!clarificationAnswer.trim() && !clarRecorder.audioBlob}
                  submitTitle={isOutOfScope ? 'Отправить новый запрос' : 'Ответить на вопрос'}
                  autoFocus
                />
              </div>}

            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AnatomyAssistantPirogovDev;